import { useState } from "react";

/**
 * **The last answer, whatever it answered** — RTK Query's `data`, which the
 * donor's spec screens read: `currentState.isSuccess ? currentState.data :
 * lastResult?.data` (`@reduxjs/toolkit` `rtk-query-react.modern.mjs:146`). A
 * new query whose read fails leaves the previous answer on screen, where
 * TanStack's `keepPreviousData` covers only the wait. `undefined` until a read
 * has answered.
 *
 * Held from each query's own answer, never a placeholder standing in for it,
 * and once per answer — per `key` and fetch — so the adjustment, made during
 * render, runs once and cannot loop. `key` names the query the answer is for.
 *
 * **A placeholder is returned as it stands.** It is `keepPreviousData`'s
 * previous answer, the one this would otherwise hand back, and while it is
 * on screen its readers draw it as the donor draws a new query's wait — the
 * list under its skeletons (`isPlaceholderData` is their `loading`), the value
 * list as the rows the search replaces. Only a query with no data at all, a
 * failed one, falls back to the kept answer.
 *
 * Its readers: the Spec list and the single-community table (#554 D3,
 * `SpecsListTableIsland/data/loader.ts:19-31`), and the column filter's value
 * list, whose failed search keeps the previous rows (and auto-checks them).
 */
export function useLastAnswer<T>(
  query: { data: T | undefined; isPlaceholderData: boolean; dataUpdatedAt: number },
  key: string,
): T | undefined {
  const [kept, setKept] = useState<{ key: string; at: number; answer: T } | null>(null);
  if (
    query.data !== undefined &&
    !query.isPlaceholderData &&
    (kept?.key !== key || kept.at !== query.dataUpdatedAt)
  ) {
    setKept({ key, at: query.dataUpdatedAt, answer: query.data });
  }
  return query.data ?? kept?.answer;
}
